// Prerequisite-skills graph for the Chain Analysis tab. Cluster-scoped, all
// years visible, skills as react-flow nodes with prereq edges between them.
//
// Two view modes in the dropdown:
//   - Curriculum         : every skill in the cluster (default)
//   - Student attempted  : only skills the student has a BASE attempt for
//
// The cross-cluster toggle defaults on. Turning it off hides nodes whose
// strand_id differs from the selected cluster, along with any edges that
// connect to them.
//
// Layout: years stacked vertically (highest year top, Year 1 bottom);
// skills sorted by skill_order within each year row.

import React from "react"
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  useStore,
} from "reactflow"
import type {
  Edge,
  EdgeProps,
  Node,
  NodeProps,
} from "reactflow"
import "reactflow/dist/style.css"
import { BMD_CHAIN_STOPPED_PROPAGATES } from "../constants/bmd.constants"
// Supplied by context so the graph serves whichever subject the dashboard is showing.
import { useReportFetchers } from "../services/report-fetchers"
import type {
  BmdPrereqGraphAttemptStatus,
  BmdPrereqGraphEdge,
  BmdPrereqGraphNode,
  BmdPrereqGraphStrand,
  GetStudentPrereqGraphResponse,
} from "../types/bmd-report.types"
import { plainText } from "../utils/formatters"

// ============================================================================
// Layout constants
// ============================================================================

const ROW_HEIGHT = 110
const COL_WIDTH = 130
const X_OFFSET = 120 // leaves room on the left for year-row labels
const Y_OFFSET = 20

/**
 * The tone a node is drawn in.
 *
 * "chain_stopped" is a DISPLAY status, not something the API returns. Those
 * skills were never attempted, so the server rightly calls them not_attempted,
 * but lumping them in with the rest is misleading: they went untested because
 * the student got the skill above them RIGHT and the chain stopped, not because
 * anything is unknown about them. Orange reads as "still to do" on a skill
 * there is no reason to test.
 */
type DisplayStatus = BmdPrereqGraphAttemptStatus | "chain_stopped"

const STATUS_STYLE: Record<
  DisplayStatus,
  { ring: string; bg: string; label: string }
> = {
  correct: { ring: "#15803d", bg: "#22c55e", label: "Correct" },
  wrong: { ring: "#b91c1c", bg: "#ef4444", label: "Wrong" },
  // Default curriculum-view tone, matching the reference orange.
  not_attempted: { ring: "#c2410c", bg: "#fb923c", label: "Not attempted" },
  // Blue, and deliberately not the purple used for the selected ring.
  chain_stopped: {
    ring: "#1d4ed8",
    bg: "#60a5fa",
    label: "Chain stopped (correct above)",
  },
}

/**
 * Skills the chain never reached, because it stopped at a correct answer
 * somewhere above them.
 *
 * An edge runs from a skill TO its prerequisite, so the skills that would have
 * driven a test of N are the `from` ends of the edges pointing at N. If one of
 * those was answered correctly, the chain stopped there and N was never
 * reached.
 *
 * IT PROPAGATES ALL THE WAY DOWN, while BMD_CHAIN_STOPPED_PROPAGATES is on. A
 * skill the chain never got to is also a skill whose own prerequisites it never
 * got to, so this is the downward closure from those first stopped skills, not
 * just one level of them. Marking only the first level would leave a blue node
 * sitting on top of a column of orange ones that went untested for exactly the
 * same reason. The flag exists because that is a judgement call the PM may want
 * back: turning it off marks only the skills directly under a correct answer.
 *
 * A node with a WRONG dependent is excluded at every level, even if it also has
 * a correct or unreached one: that chain should have descended into it, so its
 * non-attempt has some other cause - an unrun test, or the depth cap - and
 * claiming otherwise would be a guess.
 *
 * Computed from the FULL payload, never the filtered view, so toggling
 * cross-cluster edges cannot change what colour a node is.
 */
function chainStoppedSkillIds(
  nodes: BmdPrereqGraphNode[],
  edges: BmdPrereqGraphEdge[],
): Set<number> {
  const statusById = new Map<number, BmdPrereqGraphAttemptStatus>()
  for (const n of nodes) statusById.set(n.skill_id, n.attempt_status)

  const hasCorrectDependent = new Set<number>()
  const hasWrongDependent = new Set<number>()
  // skill -> the prerequisites underneath it, for walking downwards.
  const prereqsOf = new Map<number, number[]>()
  for (const e of edges) {
    const dependent = statusById.get(e.from_skill_id)
    if (dependent === "correct") hasCorrectDependent.add(e.to_skill_id)
    if (dependent === "wrong") hasWrongDependent.add(e.to_skill_id)
    const list = prereqsOf.get(e.from_skill_id)
    if (list) list.push(e.to_skill_id)
    else prereqsOf.set(e.from_skill_id, [e.to_skill_id])
  }

  const unreachable = (id: number): boolean =>
    statusById.get(id) === "not_attempted" && !hasWrongDependent.has(id)

  const out = new Set<number>()
  const queue: number[] = []
  for (const n of nodes) {
    if (!unreachable(n.skill_id)) continue
    if (!hasCorrectDependent.has(n.skill_id)) continue
    out.add(n.skill_id)
    queue.push(n.skill_id)
  }

  // Off: only the skills sitting directly under a correct answer are marked.
  if (!BMD_CHAIN_STOPPED_PROPAGATES) return out

  // The `out` guard also terminates cycles, which the prerequisite data is not
  // guaranteed to be free of.
  while (queue.length > 0) {
    const id = queue.shift() as number
    for (const prereq of prereqsOf.get(id) || []) {
      if (out.has(prereq) || !unreachable(prereq)) continue
      out.add(prereq)
      queue.push(prereq)
    }
  }
  return out
}

// Hidden handle styling: present so edges anchor, but invisible.
const HIDDEN_HANDLE: React.CSSProperties = {
  width: 1,
  height: 1,
  background: "transparent",
  border: "none",
  pointerEvents: "none",
}

// ============================================================================
// Floating edge. Computes line endpoints at the boundary of each node so the
// edges touch the side of each circle facing the other endpoint, instead of
// all anchoring to the same fixed handle.
// ============================================================================

type RfNodeLike = {
  id: string
  type?: string
  positionAbsolute?: { x: number; y: number }
  position: { x: number; y: number }
  width?: number | null
  height?: number | null
}

function nodeCenter(n: RfNodeLike) {
  const pos = n.positionAbsolute || n.position
  return { x: pos.x + (n.width || 36) / 2, y: pos.y + (n.height || 36) / 2 }
}

// Point on `fromNode`'s circumference pointing toward `toward` (a center
// point), so an edge segment visibly touches the side of the circle facing
// the other endpoint (or, for curved edges, facing the bezier control point).
function circumferencePoint(fromNode: RfNodeLike, toward: { x: number; y: number }) {
  const c = nodeCenter(fromNode)
  const dx = toward.x - c.x
  const dy = toward.y - c.y
  const dist = Math.sqrt(dx * dx + dy * dy) || 1
  const r = (fromNode.width || 36) / 2
  return { x: c.x + (dx / dist) * r, y: c.y + (dy / dist) * r }
}

function pointToSegmentDistance(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
) {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

const FloatingEdge: React.FC<EdgeProps> = ({
  id,
  source,
  target,
  markerEnd,
  style,
}) => {
  // Subscribe to the whole node map so the edge re-renders when layout
  // changes. Needed for the obstacle check below.
  const nodeInternals = useStore((store) => store.nodeInternals)
  const sourceNode = nodeInternals.get(source) as RfNodeLike | undefined
  const targetNode = nodeInternals.get(target) as RfNodeLike | undefined
  if (!sourceNode || !targetNode) return null

  const sc = nodeCenter(sourceNode)
  const tc = nodeCenter(targetNode)

  // Does the straight segment from sc to tc pass through, or graze, any other
  // skill node's circle? If so, arc around it.
  let obstacle: { x: number; y: number } | null = null
  for (const n of nodeInternals.values() as IterableIterator<RfNodeLike>) {
    if (n.id === source || n.id === target || n.type !== "skill") continue
    const c = nodeCenter(n)
    const clearance = (n.width || 36) / 2 + 6
    if (pointToSegmentDistance(c, sc, tc) < clearance) {
      obstacle = c
      break
    }
  }

  let d: string
  if (obstacle) {
    // Bow the curve perpendicular to the segment, away from the obstacle.
    const mx = (sc.x + tc.x) / 2
    const my = (sc.y + tc.y) / 2
    const dx = tc.x - sc.x
    const dy = tc.y - sc.y
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    // Two perpendicular directions; pick the one that moves AWAY from the
    // obstacle's side of the line.
    const px = -dy / len
    const py = dx / len
    const obstacleSide = (obstacle.x - mx) * px + (obstacle.y - my) * py
    const sign = obstacleSide > 0 ? -1 : 1
    const offset = Math.min(70, Math.max(36, len * 0.18))
    const cx = mx + px * sign * offset
    const cy = my + py * sign * offset
    const s = circumferencePoint(sourceNode, { x: cx, y: cy })
    const t = circumferencePoint(targetNode, { x: cx, y: cy })
    d = `M ${s.x} ${s.y} Q ${cx} ${cy} ${t.x} ${t.y}`
  } else {
    const s = circumferencePoint(sourceNode, tc)
    const t = circumferencePoint(targetNode, sc)
    d = `M ${s.x} ${s.y} L ${t.x} ${t.y}`
  }

  return (
    <path
      id={id}
      className="react-flow__edge-path"
      d={d}
      markerEnd={markerEnd}
      style={style}
      fill="none"
    />
  )
}

const EDGE_TYPES = { floating: FloatingEdge } as const

// ============================================================================
// Selection / highlight
// ============================================================================

type NodeHighlight = "none" | "selected" | "prereq" | "dependent" | "dimmed"

// Edge colours used when a node is selected.
// The "Curriculum (all skills)" view is built but not offered. The code path
// and its state are left intact, so putting it back is flipping this to true.
// Worth keeping, because this feature set has already reversed once.
const SHOW_CURRICULUM_VIEW = false

const PREREQ_EDGE_COLOR = "#f59e0b" // amber — chain DOWN to prerequisites
// Ring for a node from another cluster, drawn as an OUTLINE outside the node's
// own border.
//
// It cannot share the border with the status: selecting a node, or hovering one
// that is a prerequisite or a dependent, replaces the border colour, and a
// cross-cluster node would then stop looking cross-cluster at the moment the
// tutor interacted with it. An outline sits outside the border, so both show at
// once.
//
// Near-black on purpose. Every other colour here is taken: green, red, orange
// and blue are the four statuses, amber and green are the prerequisite and
// dependent highlights, and purple is the selected ring - which is what this
// was first set to, making a cross-cluster node identical to a selected one.
const CROSS_STRAND_RING = "#111827"
const CROSS_STRAND_RING_WIDTH = 5
const CROSS_STRAND_RING_STYLE = "solid"
// Sits FLUSH against the node: outlineOffset 0. Any offset leaves a ring
// floating away from the circle, which reads as two shapes rather than one
// thick border.
const CROSS_STRAND_RING_OFFSET = 0
const DEPENDENT_EDGE_COLOR = "#16a34a" // green — chain UP to dependents
const DIMMED_EDGE_COLOR = "#d1d5db"
const SELECTED_RING_COLOR = "#7c3aed" // purple ring on the clicked node

// BFS closure over an adjacency map starting from `start` (inclusive).
function reachable(start: number, adj: Map<number, number[]>): Set<number> {
  const seen = new Set<number>([start])
  const queue: number[] = [start]
  while (queue.length) {
    const id = queue.shift() as number
    for (const next of adj.get(id) || []) {
      if (!seen.has(next)) {
        seen.add(next)
        queue.push(next)
      }
    }
  }
  return seen
}

// ============================================================================
// Custom node types
// ============================================================================

type SkillNodeData = BmdPrereqGraphNode & {
  // True when the node sits outside the selected cluster: a shadow node,
  // fetched only because a cross-cluster edge points at it.
  is_outside_strand: boolean
  // Set during selection. Drives the ring colour and the dimming.
  highlight: NodeHighlight
  // What the node is DRAWN as. Equals attempt_status except where the chain
  // stopped above it; see chainStoppedSkillIds.
  display_status: DisplayStatus
}

const SkillNode: React.FC<NodeProps<SkillNodeData>> = ({ data }) => {
  const status = STATUS_STYLE[data.display_status] || STATUS_STYLE.not_attempted
  // A cross-cluster node keeps its STATUS fill; the cross-cluster marker is a
  // separate outline (see CROSS_STRAND_RING). Greying the fill out, as this
  // once did, threw away the one thing a tutor wants from a shadow node:
  // whether the student got it right.
  let ring = status.ring
  let ringWidth = 2
  let opacity = data.is_outside_strand ? 0.95 : 1
  switch (data.highlight) {
    case "selected":
      ring = SELECTED_RING_COLOR
      ringWidth = 4
      opacity = 1
      break
    case "prereq":
      ring = PREREQ_EDGE_COLOR
      ringWidth = 3
      opacity = 1
      break
    case "dependent":
      ring = DEPENDENT_EDGE_COLOR
      ringWidth = 3
      opacity = 1
      break
    case "dimmed":
      opacity = 0.18
      break
    default:
      break
  }
  return (
    <div style={{ width: 36, height: 36, position: "relative" }}>
      {/* Hidden handles. React-flow needs them to register the node as a
          valid edge endpoint, but the floating-edge component computes the
          actual line endpoints from the circle's circumference. */}
      <Handle type="target" position={Position.Top} id="t" style={HIDDEN_HANDLE} />
      <Handle type="source" position={Position.Bottom} id="b" style={HIDDEN_HANDLE} />

      <div
        className="rounded-circle"
        style={{
          width: 36,
          height: 36,
          backgroundColor: status.bg,
          border: `${ringWidth}px solid ${ring}`,
          // Outside the border, so the status ring and the highlight ring both
          // keep working on a cross-cluster node.
          outline: data.is_outside_strand
            ? `${CROSS_STRAND_RING_WIDTH}px ${CROSS_STRAND_RING_STYLE} ${CROSS_STRAND_RING}`
            : undefined,
          outlineOffset: data.is_outside_strand
            ? CROSS_STRAND_RING_OFFSET
            : undefined,
          opacity,
          cursor: "pointer",
        }}
      ></div>
    </div>
  )
}

// Detail card shown when a node is hovered. It renders as a top-right overlay
// inside the canvas container rather than inside the node, so react-flow's
// pointer handling does not suppress it.
const HoverDetailCard: React.FC<{ data: SkillNodeData }> = ({ data }) => {
  const status = STATUS_STYLE[data.display_status] || STATUS_STYLE.not_attempted
  return (
    <div
      className="shadow"
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        minWidth: 260,
        maxWidth: 320,
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 12,
        fontSize: 12,
        lineHeight: 1.4,
        color: "#1f2a44",
        zIndex: 1000,
        pointerEvents: "none",
      }}
    >
      <div className="fw-bold mb-1">
        {plainText(data.skill_name) || "Unnamed skill"}
      </div>
      <div className="text-muted mb-2">
        {data.year_level_name || "—"}
        {data.topic_name ? ` · ${plainText(data.topic_name)}` : ""}
      </div>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {data.strand_name && (
          <span
            className="badge"
            style={{
              // Violet, matching the node's border. It was red, which now
              // reads as the "wrong" status the fill is using.
              backgroundColor: data.is_outside_strand ? "#ede9fe" : "#dbeafe",
              color: data.is_outside_strand ? "#5b21b6" : "#1d4ed8",
            }}
          >
            {data.strand_name}
            {data.is_outside_strand ? " (other cluster)" : ""}
          </span>
        )}
        {data.sub_strand_name && (
          <span
            className="badge"
            style={{ backgroundColor: "#e5e7eb", color: "#374151" }}
          >
            {data.sub_strand_name}
          </span>
        )}
      </div>
      <div className="text-muted">
        Status: <span style={{ color: status.ring }}>{status.label}</span>
      </div>
    </div>
  )
}

// Persistent panel shown while a node is selected: the clicked skill plus its
// direct prerequisites and direct dependents. Rendered as a top-right overlay
// inside the canvas container.
const SelectedDetailPanel: React.FC<{
  node: SkillNodeData
  directPrereqs: SkillNodeData[]
  directDependents: SkillNodeData[]
  onClose: () => void
}> = ({ node, directPrereqs, directDependents, onClose }) => {
  const status = STATUS_STYLE[node.display_status] || STATUS_STYLE.not_attempted
  return (
    <div
      className="shadow"
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        width: 300,
        maxHeight: "calc(100% - 24px)",
        overflowY: "auto",
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 14,
        fontSize: 12,
        lineHeight: 1.4,
        color: "#1f2a44",
        zIndex: 1000,
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="text-uppercase text-muted fw-bold font-size-11">
          Selected skill
        </div>
        <button
          type="button"
          className="btn btn-sm btn-link p-0 text-muted"
          onClick={onClose}
          aria-label="Clear selection"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="fw-bold mb-1">{plainText(node.skill_name) || "Unnamed skill"}</div>
      <div className="text-muted mb-2">
        {node.year_level_name || "—"}
        {node.topic_name ? ` · ${plainText(node.topic_name)}` : ""}
      </div>
      <div className="d-flex flex-wrap gap-1 mb-3">
        {node.strand_name && (
          <span
            className="badge"
            style={{ backgroundColor: "#dbeafe", color: "#1d4ed8" }}
          >
            {node.strand_name}
          </span>
        )}
        {node.sub_strand_name && (
          <span
            className="badge"
            style={{ backgroundColor: "#e5e7eb", color: "#374151" }}
          >
            {node.sub_strand_name}
          </span>
        )}
        <span
          className="badge"
          style={{ backgroundColor: status.bg, color: "white" }}
        >
          {status.label}
        </span>
      </div>

      <div className="text-uppercase text-muted fw-bold font-size-11 mb-1">
        Prerequisites ({directPrereqs.length})
        <span
          className="ms-1"
          style={{ color: PREREQ_EDGE_COLOR }}
          aria-hidden="true"
        >
          ●
        </span>
      </div>
      {directPrereqs.length === 0 ? (
        <div className="text-muted mb-3">None mapped.</div>
      ) : (
        <ul className="list-unstyled mb-3">
          {directPrereqs.map((p) => (
            <li key={p.skill_id} className="mb-1">
              <span className="fw-semibold">
                {plainText(p.skill_name) || "Skill (unmapped)"}
              </span>
              {p.year_level_name ? (
                <span className="text-muted"> · {p.year_level_name}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <div className="text-uppercase text-muted fw-bold font-size-11 mb-1">
        Who needs this ({directDependents.length})
        <span
          className="ms-1"
          style={{ color: DEPENDENT_EDGE_COLOR }}
          aria-hidden="true"
        >
          ●
        </span>
      </div>
      {directDependents.length === 0 ? (
        <div className="text-muted mb-0">None mapped.</div>
      ) : (
        <ul className="list-unstyled mb-0">
          {directDependents.map((d) => (
            <li key={d.skill_id} className="mb-1">
              <span className="fw-semibold">
                {plainText(d.skill_name) || "Skill (unmapped)"}
              </span>
              {d.year_level_name ? (
                <span className="text-muted"> · {d.year_level_name}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

type YearLabelNodeData = { label: string }

const YearLabelNode: React.FC<NodeProps<YearLabelNodeData>> = ({ data }) => (
  <div
    className="text-uppercase fw-bold font-size-12 text-muted"
    style={{ minWidth: 80, textAlign: "right", paddingRight: 12 }}
  >
    {data.label}
  </div>
)

const NODE_TYPES = {
  skill: SkillNode,
  yearLabel: YearLabelNode,
} as const

// ============================================================================
// Main component
// ============================================================================

export type BmdPrereqGraphProps = {
  studentKey: string
  /**
   * The year the diagnostic was set at. The tree is capped to it, so a Year 4
   * test does not draw Year 5 and Year 6 skills, nor the cross-cluster
   * prerequisites hanging off them.
   */
  baseYearLevelId?: number | null
  /**
   * The diagnostic the report is scoped to. The node colours come from it alone, so the graph
   * answers "how did THIS test go" like everything around it. Null means the whole year level.
   */
  diagnosticsSessionKey?: string | null
  // Optional fixed height for the graph canvas; defaults to 520px.
  height?: number
}

export const BmdPrereqGraph: React.FC<BmdPrereqGraphProps> = ({
  studentKey,
  baseYearLevelId = null,
  diagnosticsSessionKey = null,
  height = 520,
}) => {
  const [data, setData] = React.useState<GetStudentPrereqGraphResponse | null>(
    null,
  )
  const [strandId, setStrandId] = React.useState<number | null>(null)
  // Always the student's own attempts. The curriculum view was dropped as an
  // option because it buried the student's real results in several hundred
  // untouched skills. See SHOW_CURRICULUM_VIEW.
  const [viewMode, setViewMode] = React.useState<
    "curriculum" | "attempted"
  >("attempted")
  const [showCrossStrand, setShowCrossStrand] = React.useState<boolean>(true)
  // In "Student attempted only" view, whether to also pull in the skills that
  // build on the attempted ones, the up-chain. Off by default because it
  // clutters the canvas. The click-highlight still surfaces the up-chain in
  // green when this is on.
  const [showBuildsOn, setShowBuildsOn] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<boolean>(false)
  // The hovered node, which drives the detail card overlay. Set from
  // react-flow's onNodeMouseEnter rather than per-node mouseenter handlers,
  // which were not reaching the inner div.
  const [hoveredNode, setHoveredNode] = React.useState<SkillNodeData | null>(
    null,
  )
  // The clicked node. When set, its prereq chain (down, amber) and dependent
  // chain (up, green) light up and the rest dims. Clicking another node in the
  // chain re-roots it; clicking empty canvas clears it.
  const [selectedSkillId, setSelectedSkillId] = React.useState<number | null>(
    null,
  )
  const [error, setError] = React.useState<string | null>(null)

  // Reset selection when the strand changes (the previous node won't exist).
  React.useEffect(() => {
    setSelectedSkillId(null)
  }, [strandId])

  const { getStudentPrereqGraph } = useReportFetchers()

  // Initial fetch: strands list (no strand_id) + auto-pick first.
  React.useEffect(() => {
    if (!studentKey) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    getStudentPrereqGraph(
      {
        student_key: studentKey,
        strand_id: null,
        year_level_id: baseYearLevelId,
        diagnostics_session_key: diagnosticsSessionKey,
      },
      controller.signal,
    )
      .then((resp) => {
        if (controller.signal.aborted) return
        setData(resp)
        if (resp.strands.length > 0) {
          setStrandId(resp.strands[0].strand_id)
        } else {
          setLoading(false)
        }
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load prerequisite graph")
        setLoading(false)
      })
    return () => controller.abort()
  }, [studentKey, baseYearLevelId, diagnosticsSessionKey])

  // Re-fetch when strand changes.
  React.useEffect(() => {
    if (!studentKey || strandId == null) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    getStudentPrereqGraph(
      {
        student_key: studentKey,
        strand_id: strandId,
        year_level_id: baseYearLevelId,
        diagnostics_session_key: diagnosticsSessionKey,
      },
      controller.signal,
    )
      .then((resp) => {
        if (controller.signal.aborted) return
        setData(resp)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load prerequisite graph")
        setLoading(false)
      })
    return () => controller.abort()
  }, [studentKey, strandId, baseYearLevelId, diagnosticsSessionKey])

  const { rfNodes, rfEdges, selectedDetail } = React.useMemo(() => {
    if (!data || strandId == null) {
      return {
        rfNodes: [] as Node[],
        rfEdges: [] as Edge[],
        selectedDetail: null as {
          node: SkillNodeData
          directPrereqs: SkillNodeData[]
          directDependents: SkillNodeData[]
        } | null,
      }
    }

    // Worked out before any filtering, so the cross-cluster toggle cannot
    // change a node's colour.
    const chainStopped = chainStoppedSkillIds(data.nodes, data.edges)

    // Filter: cross-strand toggle drops outside-strand nodes + their edges.
    let nodes = data.nodes.map((n) => ({
      ...n,
      is_outside_strand: n.strand_id !== strandId,
      display_status: chainStopped.has(n.skill_id)
        ? ("chain_stopped" as DisplayStatus)
        : (n.attempt_status as DisplayStatus),
    }))
    let edges = data.edges
    if (!showCrossStrand) {
      const inStrandIds = new Set(
        nodes.filter((n) => !n.is_outside_strand).map((n) => n.skill_id),
      )
      nodes = nodes.filter((n) => !n.is_outside_strand)
      edges = edges.filter(
        (e) =>
          inStrandIds.has(e.from_skill_id) && inStrandIds.has(e.to_skill_id),
      )
    }
    if (viewMode === "attempted") {
      // Start from skills the student actually attempted, then include the
      // FULL chain in both directions:
      //   - down: every foundational skill the attempts depend on
      //   - up:   every skill that builds on the attempted ones
      // so the tutor sees the attempted skills in the context of where they
      // sit in the curriculum, not just the attempts in isolation.
      const attemptedIds = new Set(
        nodes
          .filter((n) => n.attempt_status !== "not_attempted")
          .map((n) => n.skill_id),
      )
      const adjDown = new Map<number, number[]>() // skill -> its prereqs
      const adjUp = new Map<number, number[]>() // skill -> skills requiring it
      for (const e of edges) {
        if (!adjDown.has(e.from_skill_id)) adjDown.set(e.from_skill_id, [])
        adjDown.get(e.from_skill_id)!.push(e.to_skill_id)
        if (!adjUp.has(e.to_skill_id)) adjUp.set(e.to_skill_id, [])
        adjUp.get(e.to_skill_id)!.push(e.from_skill_id)
      }
      const multiSourceClosure = (adj: Map<number, number[]>): Set<number> => {
        const seen = new Set<number>(attemptedIds)
        const q = Array.from(attemptedIds)
        while (q.length > 0) {
          const id = q.shift() as number
          for (const next of adj.get(id) || []) {
            if (!seen.has(next)) {
              seen.add(next)
              q.push(next)
            }
          }
        }
        return seen
      }
      const downIds = multiSourceClosure(adjDown)
      const visibleIds = new Set<number>()
      for (const id of downIds) visibleIds.add(id)
      if (showBuildsOn) {
        const upIds = multiSourceClosure(adjUp)
        for (const id of upIds) visibleIds.add(id)
      }
      nodes = nodes.filter((n) => visibleIds.has(n.skill_id))
      edges = edges.filter(
        (e) =>
          visibleIds.has(e.from_skill_id) && visibleIds.has(e.to_skill_id),
      )
    }

    // Adjacency over the (filtered) edge set:
    //   adjPrereq[skill]    -> skills it directly depends on   (chain DOWN)
    //   adjDependent[skill] -> skills that directly require it (chain UP)
    const adjPrereq = new Map<number, number[]>()
    const adjDependent = new Map<number, number[]>()
    for (const e of edges) {
      if (!adjPrereq.has(e.from_skill_id)) adjPrereq.set(e.from_skill_id, [])
      adjPrereq.get(e.from_skill_id)!.push(e.to_skill_id)
      if (!adjDependent.has(e.to_skill_id)) adjDependent.set(e.to_skill_id, [])
      adjDependent.get(e.to_skill_id)!.push(e.from_skill_id)
    }
    const nodeBySkillId = new Map<number, (typeof nodes)[number]>()
    for (const n of nodes) nodeBySkillId.set(n.skill_id, n)

    const selActive =
      selectedSkillId != null &&
      nodes.some((n) => n.skill_id === selectedSkillId)
    const prereqSet = selActive
      ? reachable(selectedSkillId as number, adjPrereq)
      : null
    const dependentSet = selActive
      ? reachable(selectedSkillId as number, adjDependent)
      : null
    const highlightFor = (skillId: number): NodeHighlight => {
      if (!selActive) return "none"
      if (skillId === selectedSkillId) return "selected"
      if (prereqSet && prereqSet.has(skillId)) return "prereq"
      if (dependentSet && dependentSet.has(skillId)) return "dependent"
      return "dimmed"
    }

    // Group by year_level_order, descending, so the highest year is on top.
    const yearGroups = new Map<number, typeof nodes>()
    for (const n of nodes) {
      const y = n.year_level_order || 0
      if (!yearGroups.has(y)) yearGroups.set(y, [])
      yearGroups.get(y)!.push(n)
    }
    const sortedYears = Array.from(yearGroups.keys()).sort((a, b) => b - a)

    const rfNodes: Node[] = []
    sortedYears.forEach((yearOrder, rowIdx) => {
      const skills = yearGroups
        .get(yearOrder)!
        .slice()
        .sort((a, b) => a.skill_order - b.skill_order)
      // Year-label "node" at the left of the row.
      const yearName = skills[0]?.year_level_name || `Year ${yearOrder}`
      rfNodes.push({
        id: `year-${yearOrder}`,
        type: "yearLabel",
        position: { x: 0, y: Y_OFFSET + rowIdx * ROW_HEIGHT + 8 },
        data: { label: yearName },
        draggable: false,
        selectable: false,
      })
      skills.forEach((s, colIdx) => {
        rfNodes.push({
          id: `skill-${s.skill_id}`,
          type: "skill",
          position: {
            x: X_OFFSET + colIdx * COL_WIDTH,
            y: Y_OFFSET + rowIdx * ROW_HEIGHT,
          },
          data: { ...s, highlight: highlightFor(s.skill_id) },
          draggable: false,
        })
      })
    })

    const rfEdges: Edge[] = edges.map((e) => {
      let stroke: string
      let strokeWidth: number
      let markerColor: string
      let edgeOpacity = 1
      let dashArray: string | undefined = e.is_cross_strand
        ? "6 4"
        : undefined
      let zIndex = 0
      if (!selActive) {
        stroke = e.is_cross_strand ? "#dc2626" : "#9ca3af"
        strokeWidth = e.is_cross_strand ? 1.5 : 1.4
        markerColor = stroke
      } else {
        const onPrereq =
          !!prereqSet &&
          prereqSet.has(e.from_skill_id) &&
          prereqSet.has(e.to_skill_id)
        const onDependent =
          !!dependentSet &&
          dependentSet.has(e.from_skill_id) &&
          dependentSet.has(e.to_skill_id)
        if (onPrereq) {
          stroke = PREREQ_EDGE_COLOR
          strokeWidth = 2.5
          markerColor = PREREQ_EDGE_COLOR
          zIndex = 2
        } else if (onDependent) {
          stroke = DEPENDENT_EDGE_COLOR
          strokeWidth = 2.5
          markerColor = DEPENDENT_EDGE_COLOR
          zIndex = 2
        } else {
          stroke = DIMMED_EDGE_COLOR
          strokeWidth = 1
          markerColor = "#e5e7eb"
          edgeOpacity = 0.25
          dashArray = undefined
        }
      }
      return {
        id: `e-${e.from_skill_id}-${e.to_skill_id}`,
        source: `skill-${e.from_skill_id}`,
        target: `skill-${e.to_skill_id}`,
        // Custom edge type: line endpoints computed at each node's
        // circumference (not a fixed handle) + arc around obstacles.
        type: "floating",
        animated: false,
        zIndex,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: markerColor,
          width: 14,
          height: 14,
        },
        style: {
          stroke,
          strokeWidth,
          opacity: edgeOpacity,
          ...(dashArray ? { strokeDasharray: dashArray } : {}),
        },
      }
    })

    // Detail panel data for the currently-selected node.
    let selectedDetail: {
      node: SkillNodeData
      directPrereqs: SkillNodeData[]
      directDependents: SkillNodeData[]
    } | null = null
    if (selActive) {
      const baseNode = nodeBySkillId.get(selectedSkillId as number)
      if (baseNode) {
        const lookup = (ids: number[] | undefined, h: NodeHighlight) =>
          (ids || [])
            .map((id) => nodeBySkillId.get(id))
            .filter((n): n is (typeof nodes)[number] => !!n)
            .map((n) => ({ ...n, highlight: h }))
        selectedDetail = {
          node: { ...baseNode, highlight: "selected" },
          directPrereqs: lookup(
            adjPrereq.get(selectedSkillId as number),
            "prereq",
          ),
          directDependents: lookup(
            adjDependent.get(selectedSkillId as number),
            "dependent",
          ),
        }
      }
    }

    return { rfNodes, rfEdges, selectedDetail }
  }, [
    data,
    strandId,
    viewMode,
    showCrossStrand,
    showBuildsOn,
    selectedSkillId,
  ])

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div className="text-uppercase text-muted fw-bold font-size-12">
          Prerequisite Skill Tree
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3 font-size-12 text-muted">
          {data && (
            <>
              <span>
                <strong>{data.summary.skills}</strong> skills
              </span>
              <span>
                <strong>{data.summary.dependencies}</strong> dependencies
              </span>
              <span>
                <strong>{data.summary.cross_strand}</strong> cross-cluster
              </span>
            </>
          )}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
        <div className="d-flex align-items-center">
          <label
            htmlFor="bmd-graph-strand"
            className="text-muted font-size-12 me-2 mb-0"
          >
            Cluster:
          </label>
          <select
            id="bmd-graph-strand"
            className="form-select form-select-sm"
            style={{ minWidth: 200 }}
            value={strandId ?? ""}
            onChange={(e) => setStrandId(Number(e.target.value) || null)}
            disabled={!data || data.strands.length === 0}
          >
            {data?.strands.map((s: BmdPrereqGraphStrand) => (
              <option key={s.strand_id} value={s.strand_id}>
                {s.strand_name}
              </option>
            ))}
          </select>
        </div>

        {SHOW_CURRICULUM_VIEW ? (
          <div className="d-flex align-items-center">
            <label
              htmlFor="bmd-graph-view"
              className="text-muted font-size-12 me-2 mb-0"
            >
              View:
            </label>
            <select
              id="bmd-graph-view"
              className="form-select form-select-sm"
              style={{ minWidth: 180 }}
              value={viewMode}
              onChange={(e) =>
                setViewMode(e.target.value as "curriculum" | "attempted")
              }
            >
              <option value="curriculum">Curriculum (all skills)</option>
              <option value="attempted">Student attempted only</option>
            </select>
          </div>
        ) : null}

        <div className="form-check ms-2">
          <input
            id="bmd-graph-cross-strand"
            type="checkbox"
            className="form-check-input"
            checked={showCrossStrand}
            onChange={(e) => setShowCrossStrand(e.target.checked)}
          />
          <label
            htmlFor="bmd-graph-cross-strand"
            className="form-check-label font-size-12 text-muted"
          >
            Show cross-cluster edges
          </label>
        </div>

        {viewMode === "attempted" && (
          <div className="form-check ms-2">
            <input
              id="bmd-graph-builds-on"
              type="checkbox"
              className="form-check-input"
              checked={showBuildsOn}
              onChange={(e) => setShowBuildsOn(e.target.checked)}
            />
            <label
              htmlFor="bmd-graph-builds-on"
              className="form-check-label font-size-12 text-muted"
            >
              Show skills that build on these
            </label>
          </div>
        )}
      </div>

      {error ? (
        <div className="text-danger font-size-13">{error}</div>
      ) : !data || data.strands.length === 0 ? (
        <div className="text-muted font-size-13 p-4 text-center">
          No clusters available.
        </div>
      ) : (
        <div
          style={{
            height,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            backgroundColor: "#fafafa",
            position: "relative",
          }}
        >
          {loading && (
            <div
              className="text-muted font-size-13"
              style={{
                position: "absolute",
                top: 8,
                right: 12,
                zIndex: 10,
                background: "white",
                padding: "2px 8px",
                borderRadius: 4,
                border: "1px solid #e5e7eb",
              }}
            >
              Loading…
            </div>
          )}
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.3}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
            onNodeMouseEnter={(_e, node) => {
              if (node.type === "skill") {
                setHoveredNode(node.data as SkillNodeData)
              }
            }}
            onNodeMouseLeave={() => setHoveredNode(null)}
            onNodeClick={(_e, node) => {
              if (node.type === "skill") {
                const d = node.data as SkillNodeData
                // Clicking the already-selected node clears the selection.
                setSelectedSkillId((cur) =>
                  cur === d.skill_id ? null : d.skill_id,
                )
              }
            }}
            onPaneClick={() => setSelectedSkillId(null)}
          >
            <Background gap={20} color="#e5e7eb" />
            <Controls showInteractive={false} />
          </ReactFlow>
          {selectedDetail ? (
            <SelectedDetailPanel
              node={selectedDetail.node}
              directPrereqs={selectedDetail.directPrereqs}
              directDependents={selectedDetail.directDependents}
              onClose={() => setSelectedSkillId(null)}
            />
          ) : hoveredNode ? (
            <HoverDetailCard data={hoveredNode} />
          ) : null}
        </div>
      )}

      <div className="d-flex flex-wrap gap-3 mt-3 font-size-12 text-muted">
        <LegendDot color={STATUS_STYLE.correct.ring} bg={STATUS_STYLE.correct.bg} label="Correct" />
        <LegendDot color={STATUS_STYLE.wrong.ring} bg={STATUS_STYLE.wrong.bg} label="Wrong" />
        <LegendDot color={STATUS_STYLE.not_attempted.ring} bg={STATUS_STYLE.not_attempted.bg} label="Not attempted" />
        <LegendDot color={STATUS_STYLE.chain_stopped.ring} bg={STATUS_STYLE.chain_stopped.bg} label="Chain stopped (correct above)" />
        <LegendDot
          color={STATUS_STYLE.not_attempted.ring}
          bg={STATUS_STYLE.not_attempted.bg}
          outline={CROSS_STRAND_RING}
          label="Other cluster (fill still shows status)"
        />
        <span className="ms-2">
          <span style={{ color: "#9ca3af" }}>━━</span> within-cluster edge
        </span>
        <span>
          <span style={{ color: "#dc2626" }}>━ ━</span> cross-cluster edge
        </span>
      </div>
    </div>
  )
}

const LegendDot: React.FC<{
  color: string
  bg: string
  label: string
  /** Draws the cross-cluster ring, so the swatch matches the node it names. */
  outline?: string
}> = ({ color, bg, label, outline }) => (
  <span className="d-inline-flex align-items-center">
    <span
      className="rounded-circle d-inline-block"
      style={{
        width: 12,
        height: 12,
        backgroundColor: bg,
        border: `2px solid ${color}`,
        outline: outline
          ? `2px ${CROSS_STRAND_RING_STYLE} ${outline}`
          : undefined,
        outlineOffset: outline ? CROSS_STRAND_RING_OFFSET : undefined,
        // The outline sits outside the box, so it needs room of its own or it
        // overlaps the label.
        marginRight: outline ? 6 : 4,
        marginLeft: outline ? 2 : 0,
      }}
      aria-hidden="true"
    ></span>
    {label}
  </span>
)

export default BmdPrereqGraph
